import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, Progress, Button, Badge } from "@learn-me/ui";
import { api } from "@/lib/api";
import { BookOpen, PlayCircle, CheckCircle, Clock, Filter } from "lucide-react";
import type {
  ApiResponse,
  PaginatedResponse,
  Enrollment,
  Course,
} from "@learn-me/types";
import { motion } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { normalizeImageUrl } from "@/lib/imageUrl";

interface EnrollmentWithCourse extends Omit<Enrollment, "lastAccessedAt"> {
  course: Course & {
    chapters?: Array<{
      id: string;
      title: string;
      userProgress?: { isCompleted: boolean } | null;
    }>;
  };
  lastAccessedAt?: string | Date;
}

type FilterType = "all" | "in-progress" | "completed" | "not-started";

export default function MyCoursesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentFilter = (searchParams.get("filter") as FilterType) || "all";

  const { data, isLoading } = useQuery({
    queryKey: ["my-courses"],
    queryFn: async () => {
      const response = await api.get<
        ApiResponse<PaginatedResponse<EnrollmentWithCourse>>
      >("/enrollments/my-courses");
      return response.data.data;
    },
  });

  const handleFilterChange = (filter: FilterType) => {
    const newParams = new URLSearchParams(searchParams);
    if (filter === "all") {
      newParams.delete("filter");
    } else {
      newParams.set("filter", filter);
    }
    setSearchParams(newParams);
  };

  // Filter and sort enrollments
  const filteredEnrollments = data?.items
    ?.filter((enrollment) => {
      switch (currentFilter) {
        case "in-progress":
          return enrollment.progress > 0 && enrollment.progress < 100;
        case "completed":
          return enrollment.progress === 100;
        case "not-started":
          return enrollment.progress === 0;
        default:
          return true;
      }
    })
    // Sort by lastAccessedAt (most recent first), then by enrolledAt
    .sort((a, b) => {
      const dateA = a.lastAccessedAt
        ? new Date(a.lastAccessedAt)
        : new Date(a.enrolledAt);
      const dateB = b.lastAccessedAt
        ? new Date(b.lastAccessedAt)
        : new Date(b.enrolledAt);
      return dateB.getTime() - dateA.getTime();
    });

  const getNextChapter = (enrollment: EnrollmentWithCourse) => {
    return enrollment.course.chapters?.find(
      (ch) => !ch.userProgress?.isCompleted,
    );
  };

  const filters: { key: FilterType; label: string; count?: number }[] = [
    { key: "all", label: "Todos", count: data?.items?.length },
    {
      key: "in-progress",
      label: "En progreso",
      count: data?.items?.filter((e) => e.progress > 0 && e.progress < 100)
        .length,
    },
    {
      key: "completed",
      label: "Completados",
      count: data?.items?.filter((e) => e.progress === 100).length,
    },
    {
      key: "not-started",
      label: "Sin iniciar",
      count: data?.items?.filter((e) => e.progress === 0).length,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Mis Cursos</h1>
        <p className="text-muted-foreground">
          Todos los cursos en los que estás inscrito.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        {filters.map((filter) => (
          <Button
            key={filter.key}
            variant={currentFilter === filter.key ? "default" : "outline"}
            size="sm"
            onClick={() => handleFilterChange(filter.key)}
            className="gap-2"
          >
            {filter.label}
            {filter.count !== undefined && (
              <Badge
                variant={currentFilter === filter.key ? "secondary" : "outline"}
                className="ml-1 h-5 min-w-5 px-1.5"
              >
                {filter.count}
              </Badge>
            )}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="animate-pulse">
              <div className="aspect-video bg-muted" />
              <CardContent className="p-4 space-y-2">
                <div className="h-5 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !filteredEnrollments?.length ? (
        <Card>
          <CardContent className="py-16 text-center">
            <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {currentFilter === "all"
                ? "No tienes cursos aún"
                : currentFilter === "in-progress"
                  ? "No tienes cursos en progreso"
                  : currentFilter === "completed"
                    ? "No has completado ningún curso"
                    : "No tienes cursos sin iniciar"}
            </h2>
            <p className="text-muted-foreground mb-6">
              {currentFilter === "all"
                ? "Explora nuestro catálogo y encuentra el curso perfecto para ti."
                : "Intenta con otro filtro o explora nuevos cursos."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {currentFilter !== "all" && (
                <Button
                  variant="outline"
                  onClick={() => handleFilterChange("all")}
                >
                  Ver todos los cursos
                </Button>
              )}
              <Link to="/courses">
                <Button>Explorar Cursos</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEnrollments.map((enrollment, index) => {
            const nextChapter = getNextChapter(enrollment);

            return (
              <motion.div
                key={enrollment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full flex flex-col">
                  <div className="aspect-video relative bg-muted">
                    {enrollment.course.thumbnailUrl ? (
                      <img
                        src={
                          normalizeImageUrl(enrollment.course.thumbnailUrl) ??
                          ""
                        }
                        alt={enrollment.course.title}
                        className="object-cover w-full h-full"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <BookOpen className="h-12 w-12 text-muted-foreground" />
                      </div>
                    )}
                    {enrollment.progress === 100 && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <div className="text-center text-white">
                          <CheckCircle className="h-12 w-12 mx-auto mb-2" />
                          <span className="font-semibold">Completado</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4 flex-1 flex flex-col">
                    <h3 className="font-semibold mb-2 line-clamp-2">
                      {enrollment.course.title}
                    </h3>

                    {/* Next chapter info */}
                    {nextChapter && enrollment.progress < 100 && (
                      <p className="text-sm text-muted-foreground mb-2 line-clamp-1">
                        Siguiente: {nextChapter.title}
                      </p>
                    )}

                    {/* Last accessed */}
                    {enrollment.lastAccessedAt && (
                      <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Último acceso:{" "}
                        {formatDistanceToNow(
                          new Date(enrollment.lastAccessedAt),
                          {
                            addSuffix: true,
                            locale: es,
                          },
                        )}
                      </p>
                    )}

                    <div className="mt-auto">
                      <div className="mb-4">
                        <div className="flex justify-between text-sm text-muted-foreground mb-1">
                          <span>Progreso</span>
                          <span>{enrollment.progress}%</span>
                        </div>
                        <Progress value={enrollment.progress} />
                      </div>

                      <Link to={`/learn/${enrollment.course.slug}`}>
                        <Button className="w-full">
                          <PlayCircle className="mr-2 h-4 w-4" />
                          {enrollment.progress === 0
                            ? "Comenzar"
                            : enrollment.progress === 100
                              ? "Revisar"
                              : "Continuar"}
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
